/**
 * DeveloperCardLayout - Shared layout component for developer pages
 *
 * Provides a centered card-based layout using @ecgrid/react-components Card
 * with responsive design and optional footer support.
 *
 * Props:
 * - children: Content to render inside the card
 * - showFooter: Whether to show the card footer (default: true)
 * - maxWidth: Maximum width of the container (default: '800px')
 */

import React from 'react';
import { Card, CardHeader, CardContent, CardFooter } from '@/components/ui';

export interface DeveloperCardLayoutProps {
  children: React.ReactNode;
  showFooter?: boolean;
  maxWidth?: string;
  header?: React.ReactNode;
  footer?: React.ReactNode;
}

export const DeveloperCardLayout: React.FC<DeveloperCardLayoutProps> = ({
  children,
  showFooter = true,
  maxWidth = '800px',
  header,
  footer,
}) => {
  return (
    <div
      className="w-full min-h-screen flex items-center justify-center p-4"
      style={{ maxWidth: '100%' }}
    >
      <div className="w-full" style={{ maxWidth }}>
        <Card className="shadow-lg">
          {header && <CardHeader>{header}</CardHeader>}

          <CardContent className="p-6 md:p-8">
            {children}
          </CardContent>

          {showFooter && footer && (
            <CardFooter className="p-4 bg-gray-50">
              {footer}
            </CardFooter>
          )}
        </Card>
      </div>
    </div>
  );
};

DeveloperCardLayout.displayName = 'DeveloperCardLayout';
